// One voice agent: the number(s) it answers, a test call in the browser, what
// it says and how (greeting, phone instructions, voice, language), what it can
// do (tools limited to the account, knowledge base it consults) and its recent
// calls. Backend: /api/voice-agents/{id}, /api/ai-voice/voice-tools,
// /api/voice-numbers/{number}/agent.

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowRight, FaChevronRight, FaPhoneAlt, FaTimes } from 'react-icons/fa';
import VoicePreview from './VoicePreview';
import {
    DEFAULT_VOICE, GROUP_LABELS, LANGUAGES, LANGUAGE_LABELS, REALTIME_VOICES, SPEEDS, SPEED_LABELS, TOOL_GROUPS,
    canManage, deleteAgent, fail, formatDuration, formatWhen, getAgent, getVoiceTools, setAgent, updateAgent,
} from './api';
import './VoiceSetup.css';

const formOf = (agent) => ({
    name: agent.name || '',
    greeting: agent.greeting || '',
    instructions: agent.instructions || '',
    prosody: agent.prosody || '',
    voice: agent.voice || '',
    speed: agent.speed !== null && agent.speed !== undefined ? String(agent.speed) : '',
    language: agent.language || '',
    memory_enabled: Boolean(agent.memory_enabled),
    selected_voice_tool_ids: agent.selected_voice_tool_ids || [],
    knowledge_agent_id: agent.knowledge_agent_id || '',
});

const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);

const VoiceAgentPage = () => {
    const { agentId } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const manage = canManage();
    const [agent, setAgentView] = useState(null);
    const [tools, setTools] = useState([]);
    const [form, setForm] = useState(null);
    const [saved, setSaved] = useState(null);
    const [error, setError] = useState('');
    const [feedback, setFeedback] = useState(null);
    const [saving, setSaving] = useState(false);
    const [routing, setRouting] = useState(false);
    const [confirmTemplate, setConfirmTemplate] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [showProsody, setShowProsody] = useState(false);

    const apply = (view) => {
        const next = formOf(view);
        setAgentView(view);
        setForm(next);
        setSaved(next);
        setShowProsody(Boolean(next.prosody));
    };

    useEffect(() => {
        let alive = true;
        Promise.all([getAgent(agentId), getVoiceTools()])
            .then(([agentRes, toolsRes]) => {
                if (!alive) return;
                apply(agentRes.data);
                setTools(toolsRes.data.voice_tools || []);
            })
            .catch((err) => alive && setError(fail(err, t('voiceSetup.agent.loadError', 'L’agent vocal n’a pas pu être chargé.'))));
        return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agentId]);

    const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
    const dirty = form && saved && JSON.stringify(form) !== JSON.stringify(saved);
    const selected = useMemo(() => new Set(form?.selected_voice_tool_ids || []), [form]);

    const toolLabel = (functionName) => {
        const tool = tools.find((x) => x.function_name === functionName);
        return t(`voiceSetup.tools.${functionName}.label`, tool?.name || functionName);
    };

    const groups = useMemo(() => {
        const visible = tools.filter((tool) => tool.offered || selected.has(tool.id));
        const byGroup = TOOL_GROUPS.map((group) => ({
            group,
            items: visible.filter((tool) => tool.offered && (tool.group || 'other') === group),
        })).filter((g) => g.items.length > 0);
        const leftovers = visible.filter((tool) => !tool.offered);
        if (leftovers.length) byGroup.push({ group: 'unavailable', items: leftovers });
        return byGroup;
    }, [tools, selected]);

    if (error && !agent) {
        return (
            <div className="vs-shell">
                <p className="vs-error" role="alert">{error}</p>
                <Link className="vs-link" to="/voice-agents">{t('voiceSetup.agents.back', 'Retour aux agents vocaux')}</Link>
            </div>
        );
    }
    if (!agent || !form) return <div className="vs-shell"><p className="vs-note">{t('voiceSetup.loading', 'Chargement…')}</p></div>;

    const recommended = tools.filter((tool) => tool.offered && tool.recommended).map((tool) => tool.id);
    const missingRecommended = recommended.filter((id) => !selected.has(id));
    const offeredCount = tools.filter((tool) => tool.offered).length;
    const activeCount = tools.filter((tool) => tool.offered && selected.has(tool.id)).length;
    const knowledgeOn = tools.some((tool) => tool.function_name === 'retrieve_knowledge' && selected.has(tool.id));

    const toggleTool = (id, on) => set({
        selected_voice_tool_ids: on ? [...form.selected_voice_tool_ids, id] : form.selected_voice_tool_ids.filter((x) => x !== id),
    });

    const applyTemplate = () => {
        set({
            greeting: form.greeting || t('voiceSetup.agent.templateGreeting', 'Bonjour, vous êtes bien au service client. Je suis l’assistant vocal : comment puis-je vous aider ?'),
            instructions: t('voiceSetup.agent.templateInstructions', 'Tu réponds au téléphone pour le service client. Les appelants sont des destinataires de colis ou des clients professionnels.\n\n- Présente-toi brièvement, puis demande comment tu peux aider.\n- Pour un colis : demande le numéro de suivi, relis-le à l’appelant, puis consulte le suivi. Donne le dernier statut en une ou deux phrases, sans jargon.\n- Si le problème demande une action (colis perdu, abîmé, retour), propose d’ouvrir une demande : demande l’adresse e-mail de l’appelant, épelle-la pour la confirmer, puis crée le ticket.\n- Ne promets jamais un délai ou un remboursement que tu ne peux pas vérifier.\n- Si tu n’as pas compris, demande de répéter. N’invente rien.\n- Quand la demande est traitée ou que l’appelant dit au revoir, salue-le et raccroche.'),
        });
        setConfirmTemplate(false);
    };

    const save = async () => {
        if (!form.name.trim()) {
            setFeedback({ type: 'error', text: t('voiceSetup.agent.nameRequired', 'Donnez un nom à l’agent vocal.') });
            return;
        }
        setSaving(true);
        setFeedback(null);
        try {
            const res = await updateAgent(agentId, {
                name: form.name.trim(),
                greeting: form.greeting,
                instructions: form.instructions,
                prosody: form.prosody,
                voice: form.voice || null,
                speed: form.speed === '' ? null : parseFloat(form.speed),
                language: form.language || null,
                memory_enabled: form.memory_enabled,
                selected_voice_tool_ids: form.selected_voice_tool_ids,
                knowledge_agent_id: form.knowledge_agent_id || null,
            });
            apply(res.data);
            setFeedback({ type: 'success', text: t('voiceSetup.agent.saved', 'Agent vocal enregistré.') });
        } catch (err) {
            setFeedback({ type: 'error', text: fail(err, t('voiceSetup.agent.saveError', 'L’agent vocal n’a pas pu être enregistré.')) });
        } finally {
            setSaving(false);
        }
    };

    const route = async (number, target) => {
        setRouting(true);
        setFeedback(null);
        try {
            await setAgent(number, target);
            const res = await getAgent(agentId);
            // Keep unsaved edits: only the number data comes from the server.
            setAgentView((prev) => ({ ...prev, numbers: res.data.numbers, account_numbers: res.data.account_numbers }));
        } catch (err) {
            setFeedback({ type: 'error', text: fail(err, t('voiceSetup.agent.routeError', 'Le numéro n’a pas pu être changé.')) });
        } finally {
            setRouting(false);
        }
    };

    const remove = async () => {
        try {
            await deleteAgent(agentId);
            navigate('/voice-agents');
        } catch (err) {
            setFeedback({ type: 'error', text: fail(err, t('voiceSetup.agent.deleteError', 'L’agent vocal n’a pas pu être supprimé.')) });
            setConfirmDelete(false);
        }
    };

    const assignable = (agent.account_numbers || []).filter((n) => n.agent_id !== agent.id);
    const speedOptions = SPEEDS.includes(form.speed) || form.speed === '' ? SPEEDS : [...SPEEDS, form.speed];
    const voiceOptions = REALTIME_VOICES.includes(form.voice) || form.voice === '' ? REALTIME_VOICES : [...REALTIME_VOICES, form.voice];
    const languageOptions = LANGUAGES.includes(form.language) || form.language === '' ? LANGUAGES : [...LANGUAGES, form.language];

    return (
        <div className="vs-shell">
            <div className="vs-page">
                <nav className="vs-breadcrumb" aria-label={t('voiceSetup.agents.title', 'Agents vocaux')}>
                    <Link to="/voice-agents">{t('voiceSetup.agents.title', 'Agents vocaux')}</Link>
                    <FaChevronRight aria-hidden="true" />
                    <span>{agent.name}</span>
                </nav>

                <header className="vs-header">
                    <div>
                        <input className="vs-title-input" value={form.name} maxLength={80} disabled={!manage}
                            aria-label={t('voiceSetup.agents.name', 'Nom')} onChange={(e) => set({ name: e.target.value })} />
                        <p>
                            {agent.migrated_from_text_agent
                                ? t('voiceSetup.agent.migratedHint', 'Repris des réglages « Voix » de l’agent texte du même nom : les deux sont désormais indépendants.')
                                : t('voiceSetup.agent.subtitle', 'Ce que l’agent dit en décrochant, comment il mène l’appel et ce qu’il sait faire.')}
                        </p>
                    </div>
                    <span className={`vs-chip vs-chip--${agent.numbers.length ? 'success' : 'neutral'}`}>
                        {agent.numbers.length ? t('voiceSetup.agents.live', 'En ligne') : t('voiceSetup.agents.noNumber', 'Sans numéro')}
                    </span>
                </header>

                {feedback && <p className={`vs-banner vs-banner--${feedback.type}`} role="status">{feedback.text}</p>}

                <section className="vs-agent-line">
                    <span className="vs-number-icon" aria-hidden="true"><FaPhoneAlt /></span>
                    <div className="vs-agent-line-text">
                        {agent.numbers.length > 0 ? (
                            <>
                                <strong>{t('voiceSetup.agent.answersTitle', 'Répond au')}</strong>
                                <span className="vs-number-chips">
                                    {agent.numbers.map((n) => (
                                        <span key={n.phone_number} className="vs-number-chip">
                                            {n.display}
                                            {manage && (
                                                <button type="button" disabled={routing} onClick={() => route(n.phone_number, null)}
                                                    aria-label={t('voiceSetup.agent.unroute', 'Ne plus répondre sur le {{number}}', { number: n.display })}
                                                    title={t('voiceSetup.agent.unroute', 'Ne plus répondre sur le {{number}}', { number: n.display })}>
                                                    <FaTimes aria-hidden="true" />
                                                </button>
                                            )}
                                        </span>
                                    ))}
                                </span>
                            </>
                        ) : (
                            <>
                                <strong>{t('voiceSetup.agent.noNumber', 'Aucun numéro ne renvoie vers cet agent')}</strong>
                                <span>{t('voiceSetup.agent.noNumberHint', 'Il ne reçoit pas encore d’appels.')}</span>
                            </>
                        )}
                    </div>
                    {manage && assignable.length > 0 && (
                        <label className="vs-field vs-field--inline">
                            <span>{t('voiceSetup.agent.assign', 'Répondre aussi sur')}</span>
                            <select value="" disabled={routing} onChange={(e) => e.target.value && route(e.target.value, agent.id)}>
                                <option value="">{t('voiceSetup.agent.assignPlaceholder', 'Choisir un numéro…')}</option>
                                {assignable.map((n) => (
                                    <option key={n.phone_number} value={n.phone_number}>
                                        {n.agent_name
                                            ? t('voiceSetup.agent.assignTaken', '{{number}} (répond : {{agent}})', { number: n.display, agent: n.agent_name })
                                            : n.display}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}
                    <Link className="vs-link" to={assignable.length || agent.numbers.length ? '/voice-numbers' : '/voice-numbers?buy=1'}>
                        {assignable.length || agent.numbers.length ? t('voiceSetup.agent.manageNumbers', 'Gérer les numéros') : t('voiceSetup.agent.getNumber', 'Obtenir un numéro')} <FaArrowRight aria-hidden="true" />
                    </Link>
                </section>

                <VoicePreview agentId={agent.id} toolLabel={toolLabel} unsaved={dirty} />

                <div className="vs-agent-grid">
                    <section className="vs-card">
                        <header className="vs-card-header">
                            <div>
                                <h3>{t('voiceSetup.agent.speechTitle', 'Accueil et instructions')}</h3>
                                <p>{t('voiceSetup.agent.speechDesc', 'Ce que l’agent dit en décrochant, et comment il mène l’appel.')}</p>
                            </div>
                            {manage && !confirmTemplate && (
                                <button type="button" className="vs-link" onClick={() => (form.instructions.trim() ? setConfirmTemplate(true) : applyTemplate())}>
                                    {t('voiceSetup.agent.useTemplate', 'Partir du modèle logistique')}
                                </button>
                            )}
                        </header>
                        {confirmTemplate && (
                            <p className="vs-confirm">
                                {t('voiceSetup.agent.templateConfirm', 'Remplacer vos instructions par le modèle ?')}
                                <button type="button" className="vs-link vs-link--danger" onClick={applyTemplate}>{t('voiceSetup.agent.templateReplace', 'Remplacer')}</button>
                                <button type="button" className="vs-link" onClick={() => setConfirmTemplate(false)}>{t('voiceSetup.cancel', 'Annuler')}</button>
                            </p>
                        )}
                        <label className="vs-field">
                            <span>{t('voiceSetup.agent.greeting', 'Message d’accueil')}</span>
                            <textarea rows={2} value={form.greeting} disabled={!manage} onChange={(e) => set({ greeting: e.target.value })}
                                placeholder={t('voiceSetup.agent.greetingPlaceholder', 'Bonjour, vous êtes bien au service client. Comment puis-je vous aider ?')} />
                        </label>
                        <label className="vs-field">
                            <span>{t('voiceSetup.agent.instructions', 'Instructions au téléphone')}</span>
                            <textarea rows={10} value={form.instructions} disabled={!manage} onChange={(e) => set({ instructions: e.target.value })}
                                placeholder={t('voiceSetup.agent.instructionsPlaceholderOwn', 'Consignes courtes, faites pour l’oral : qui appelle, ce que l’agent fait, ce qu’il ne promet jamais, quand il raccroche.')} />
                        </label>
                        {!form.instructions.trim() && (
                            <p className="vs-warn">{t('voiceSetup.agent.noInstructions', 'Sans instructions, l’agent improvise : partez du modèle ou écrivez vos consignes.')}</p>
                        )}
                        <button type="button" className="vs-disclosure" aria-expanded={showProsody} onClick={() => setShowProsody(!showProsody)}>
                            {t('voiceSetup.agent.prosody', 'Style de parole (avancé)')}
                        </button>
                        {showProsody && (
                            <label className="vs-field">
                                <span>{t('voiceSetup.agent.prosodyLabel', 'Ton, rythme, pauses')}</span>
                                <textarea rows={3} value={form.prosody} disabled={!manage} onChange={(e) => set({ prosody: e.target.value })}
                                    placeholder={t('voiceSetup.agent.prosodyPlaceholder', 'Ex. : ton chaleureux et posé, phrases courtes, laisse l’appelant finir avant de répondre.')} />
                            </label>
                        )}
                    </section>

                    <section className="vs-card">
                        <header className="vs-card-header">
                            <div>
                                <h3>{t('voiceSetup.agent.voiceTitle', 'Voix')}</h3>
                                <p>{t('voiceSetup.agent.voiceDesc', 'Comment l’agent sonne. Testez avec l’essai ci-dessus.')}</p>
                            </div>
                        </header>
                        <div className="vs-voice-grid">
                            <label className="vs-field">
                                <span>{t('voiceSetup.agent.voice', 'Voix')}</span>
                                <select value={form.voice} disabled={!manage} onChange={(e) => set({ voice: e.target.value })}>
                                    <option value="">{t('voiceSetup.agent.defaultVoice', 'Par défaut ({{voice}})', { voice: capitalize(DEFAULT_VOICE) })}</option>
                                    {voiceOptions.map((v) => <option key={v} value={v}>{capitalize(v)}</option>)}
                                </select>
                            </label>
                            <label className="vs-field">
                                <span>{t('voiceSetup.agent.speed', 'Débit')}</span>
                                <select value={form.speed} disabled={!manage} onChange={(e) => set({ speed: e.target.value })}>
                                    <option value="">{t('voiceSetup.agent.speedDefault', 'Par défaut')}</option>
                                    {speedOptions.map((s) => <option key={s} value={s}>{t(`voiceSetup.agent.speeds.${s.replace('.', '_')}`, SPEED_LABELS[s] || s)}</option>)}
                                </select>
                            </label>
                            <label className="vs-field">
                                <span>{t('voiceSetup.agent.language', 'Langue')}</span>
                                <select value={form.language} disabled={!manage} onChange={(e) => set({ language: e.target.value })}>
                                    <option value="">{t('voiceSetup.agent.languageAuto', 'Celle de l’appelant')}</option>
                                    {languageOptions.map((l) => <option key={l} value={l}>{t(`voiceSetup.agent.languages.${l}`, LANGUAGE_LABELS[l] || l)}</option>)}
                                </select>
                            </label>
                        </div>
                        <label className="vs-check">
                            <input type="checkbox" checked={form.memory_enabled} disabled={!manage} onChange={(e) => set({ memory_enabled: e.target.checked })} />
                            <span>
                                <strong>{t('voiceSetup.agent.memory', 'Se souvenir des appelants')}</strong>
                                <small>{t('voiceSetup.agent.memoryHint', 'L’agent reconnaît un appelant qui rappelle (par son numéro) et reprend le contexte de ses appels précédents.')}</small>
                            </span>
                        </label>
                    </section>
                </div>

                <section className="vs-card">
                    <header className="vs-card-header">
                        <div>
                            <h3>{t('voiceSetup.agent.toolsTitle', 'Ce que l’agent sait faire')}</h3>
                            <p>{t('voiceSetup.agent.toolsCount', '{{active}} outil(s) activé(s) sur {{total}}', { active: activeCount, total: offeredCount })}</p>
                        </div>
                        {manage && missingRecommended.length > 0 && (
                            <button type="button" className="vs-link" onClick={() => set({ selected_voice_tool_ids: [...form.selected_voice_tool_ids, ...missingRecommended] })}>
                                {t('voiceSetup.agent.recommended', 'Activer la sélection recommandée')}
                            </button>
                        )}
                    </header>
                    {activeCount === 0 && (
                        <p className="vs-banner vs-banner--warning">
                            {t('voiceSetup.agent.noTools', 'Sans outil, l’agent ne peut ni consulter un suivi, ni ouvrir une demande, ni raccrocher : activez au moins la sélection recommandée.')}
                        </p>
                    )}
                    <div className="vs-tool-groups">
                        {groups.map(({ group, items }) => (
                            <fieldset key={group} className="vs-tool-group" disabled={!manage}>
                                <legend>{t(`voiceSetup.groups.${group}`, GROUP_LABELS[group] || group)}</legend>
                                {group === 'unavailable' && (
                                    <p className="vs-note">{t('voiceSetup.groups.unavailableHint', 'Activés sur cet agent mais sans effet pour ce compte (intégration absente ou autre métier) : décochez-les.')}</p>
                                )}
                                {items.map((tool) => (
                                    <label key={tool.id} className="vs-check">
                                        <input type="checkbox" checked={selected.has(tool.id)} onChange={(e) => toggleTool(tool.id, e.target.checked)} />
                                        <span>
                                            <strong>
                                                {toolLabel(tool.function_name)}
                                                {tool.recommended && <em className="vs-chip vs-chip--info">{t('voiceSetup.agent.recommendedTag', 'Recommandé')}</em>}
                                            </strong>
                                            <small>{t(`voiceSetup.tools.${tool.function_name}.hint`, tool.description || '')}</small>
                                        </span>
                                    </label>
                                ))}
                            </fieldset>
                        ))}
                    </div>
                    {knowledgeOn && (
                        <div className="vs-field vs-field--narrow">
                            <label className="vs-field">
                                <span>{t('voiceSetup.agent.knowledge', 'Base de connaissances consultée')}</span>
                                <select value={form.knowledge_agent_id} disabled={!manage} onChange={(e) => set({ knowledge_agent_id: e.target.value })}>
                                    <option value="">{t('voiceSetup.agent.knowledgeNone', 'Aucune')}</option>
                                    {(agent.knowledge_sources || []).map((k) => (
                                        <option key={k.id} value={k.id}>{t('voiceSetup.agent.knowledgeOf', 'Celle de l’agent texte « {{name}} »', { name: k.name })}</option>
                                    ))}
                                </select>
                            </label>
                            {!form.knowledge_agent_id && <small className="vs-warn">{t('voiceSetup.agent.knowledgeMissing', 'Choisissez une base : sinon l’outil ne trouve rien.')}</small>}
                        </div>
                    )}
                </section>

                <section className="vs-card">
                    <header className="vs-card-header">
                        <div>
                            <h3>{t('voiceSetup.agent.callsTitle', 'Derniers appels')}</h3>
                            <p>{t('voiceSetup.agent.callsCount', '{{count}} appel(s) sur 30 jours', { count: agent.calls_30d })}</p>
                        </div>
                        <Link className="vs-link" to="/voice-tickets">{t('voiceSetup.agent.allCalls', 'Tous les appels')} <FaArrowRight aria-hidden="true" /></Link>
                    </header>
                    {agent.recent_calls.length === 0 ? (
                        <p className="vs-note">{t('voiceSetup.agent.noCalls', 'Aucun appel pour l’instant.')}</p>
                    ) : (
                        <ul className="vs-calls">
                            {agent.recent_calls.map((call) => (
                                <li key={call.call_id}>
                                    <Link to={`/voice-tickets/${encodeURIComponent(call.call_id)}`}>
                                        <span>{formatWhen(call.started_at, i18n.language)}</span>
                                        <span className="vs-calls-caller">{call.caller || t('voiceSetup.agent.hiddenCaller', 'Numéro masqué')}</span>
                                        <span className="vs-calls-duration">{formatDuration(call.duration_seconds)}</span>
                                        <FaArrowRight aria-hidden="true" />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                {manage && (
                    <div className="vs-savebar">
                        <div className="vs-savebar-left">
                            {confirmDelete ? (
                                <span className="vs-confirm">
                                    {agent.numbers.length
                                        ? t('voiceSetup.agent.deleteConfirmRouted', 'Supprimer l’agent ? Ses numéros restent sur le compte mais ne répondront plus.')
                                        : t('voiceSetup.agent.deleteConfirm', 'Supprimer cet agent vocal ?')}
                                    <button type="button" className="vs-link vs-link--danger" onClick={remove}>{t('voiceSetup.agent.deleteDo', 'Supprimer')}</button>
                                    <button type="button" className="vs-link" onClick={() => setConfirmDelete(false)}>{t('voiceSetup.cancel', 'Annuler')}</button>
                                </span>
                            ) : (
                                <button type="button" className="vs-link vs-link--danger" onClick={() => setConfirmDelete(true)}>
                                    {t('voiceSetup.agent.delete', 'Supprimer l’agent')}
                                </button>
                            )}
                        </div>
                        {dirty && <span className="vs-note">{t('voiceSetup.agent.unsaved', 'Modifications non enregistrées')}</span>}
                        <button type="button" className="u-btn u-btn--primary" onClick={save} disabled={saving || !dirty}>
                            {saving ? t('voiceSetup.agent.saving', 'Enregistrement…') : t('voiceSetup.agent.save', 'Enregistrer')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VoiceAgentPage;
